import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import i18n from './i18n';
import { getCookie, setCookie } from '../utils/cookieUtils';

// Определение типа контекста языка
interface LanguageContextType {
    language: string;
    setLanguage: (language: string) => void;
    detectLanguageFromEmail: (email: string) => string;
}

// Создание контекста со значениями по умолчанию
const LanguageContext = createContext<LanguageContextType>({
    language: 'ru',
    setLanguage: () => { },
    detectLanguageFromEmail: () => 'ru',
});

// Экспорт хука для использования этого контекста
export const useLanguage = () => useContext(LanguageContext);

/**
 * Компонент провайдера языка
 * 
 * Предоставляет языковой контекст для всего приложения
 * Обрабатывает переключение и сохранение языка
 */
export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    // Инициализация языка из cookie или использование 'ru' как запасного варианта
    const [language, setLanguageState] = useState<string>(
        getCookie('language') || localStorage.getItem('i18nextLng') || 'ru'
    );

    // Мемоизированная функция для получения текущего языка из cookies
    const getCurrentLanguage = useCallback(() => {
        return getCookie('language') || localStorage.getItem('i18nextLng') || 'ru';
    }, []);

    // Функция для изменения языка и его сохранения
    const setLanguage = (newLanguage: string) => {
        setLanguageState(newLanguage);
        i18n.changeLanguage(newLanguage);
        localStorage.setItem('i18nextLng', newLanguage);
        setCookie('language', newLanguage, 30); // Хранить в течение 30 дней
        setCookie('i18next', newLanguage, 30); // Устанавливаем куки i18next
    };

    /**
     * Функция для определения языка из email
     * Проверяет содержит ли email строку "it" для итальянского языка,
     * или использует "_XX@" формат для других языков
     */
    const detectLanguageFromEmail = (email: string): string => {
        // По умолчанию - русский, если формат email не соответствует
        let detectedLanguage = 'ru';

        // Проверка на метку языка в email (_ru, _it и т.д.)
        const languageMatch = email.match(/_([a-z]{2})@/i);
        if (languageMatch && languageMatch[1]) {
            // Преобразуем в нижний регистр для единообразия
            const langCode = languageMatch[1].toLowerCase();

            // Принимаем только поддерживаемые языки (ru, it)
            if (['ru', 'it'].includes(langCode)) {
                detectedLanguage = langCode;
            }
        }
        // Проверяем содержит ли email "it" для итальянского языка
        else if (email.toLowerCase().includes('it')) {
            detectedLanguage = 'it';
        }

        return detectedLanguage;
    };

    // Проверка изменений в cookie/localStorage и обновление языка при необходимости
    useEffect(() => {
        // Инициализация языка при монтировании
        const savedLanguage = getCurrentLanguage();
        if (savedLanguage && savedLanguage !== language) {
            setLanguage(savedLanguage);
        }

        // Функция для проверки изменения языкового cookie
        const checkLanguageChange = () => {
            const currentLanguage = getCurrentLanguage();
            if (currentLanguage && currentLanguage !== language) {
                setLanguageState(currentLanguage);
                i18n.changeLanguage(currentLanguage);
            }
        };

        // Установка интервала для проверки изменений cookie
        const intervalId = setInterval(checkLanguageChange, 2000); // Проверка каждые 2 секунды

        // Слушаем события хранилища (для синхронизации между вкладками)
        const handleStorageChange = (event: StorageEvent) => {
            if (event.key === 'i18nextLng') {
                const newLanguage = event.newValue || 'ru';
                if (newLanguage !== language) {
                    setLanguageState(newLanguage);
                    i18n.changeLanguage(newLanguage);
                    // Обновляем cookie для синхронизации
                    setCookie('language', newLanguage, 30);
                }
            }
        };

        // Добавляем слушатель событий хранилища
        window.addEventListener('storage', handleStorageChange);

        // Функция очистки
        return () => {
            clearInterval(intervalId);
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [language, getCurrentLanguage]);

    // Значение контекста
    const value = {
        language,
        setLanguage,
        detectLanguageFromEmail,
    };

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
};

export default LanguageProvider; 